import type { AppNotification } from "@/types/notification"
import type { QueryClient } from "@tanstack/react-query"
import { ADVERTISEMENT_QUERY_KEYS } from "@/hooks/use-advertisements"
import { APP_ROUTES } from "@/constants/routes"

interface NotificationReceiveContext {
  item: AppNotification
  queryClient: QueryClient
}

interface NotificationBehavior {
  getHref?: (item: AppNotification) => string | undefined
  getCtaLabelKey?: (item: AppNotification) => string | undefined
  getDisplayType?: (item: AppNotification) => AppNotification["type"] | undefined
  onReceive?: (context: NotificationReceiveContext) => void
}

function getStringData(item: AppNotification, key: string): string | undefined {
  const value = item.data?.[key]
  return typeof value === "string" && value.length > 0 ? value : undefined
}

const GAS_ACCOUNT_DEPOSIT_HREF = `${APP_ROUTES.gasAccount}?openDeposit=true`

// Current backend types: balances_percent, balances_usd, expiration, advertisement_decision.
// advertisement_decision invalidates ad cache and links to ad detail.
// balance alerts link to Gas Account with deposit modal pre-opened; expiration stays passive.
const notificationBehaviors: Record<string, NotificationBehavior> = {
  advertisement_decision: {
    getHref: (item) => {
      const advertisementId = getStringData(item, "advertisement_id")
      return advertisementId ? APP_ROUTES.adDetails(advertisementId) : undefined
    },
    getCtaLabelKey: () => "layout.notificationCtaViewAd",
    getDisplayType: (item) => {
      const decision = (
        getStringData(item, "decision") ??
        getStringData(item, "status") ??
        getStringData(item, "result")
      )?.toLowerCase()
      if (decision === "approved") return "success"
      if (decision === "rejected") return "error"
      return undefined
    },
    onReceive: ({ item, queryClient }) => {
      const advertisementId = getStringData(item, "advertisement_id")

      queryClient.invalidateQueries({
        queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENTS_LIST,
      })

      if (!advertisementId) return

      queryClient.invalidateQueries({
        queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENT_DETAIL(advertisementId),
      })
      queryClient.invalidateQueries({
        queryKey: ADVERTISEMENT_QUERY_KEYS.ADVERTISEMENT_STATS_PREFIX(
          advertisementId
        ),
      })
    },
  },
  balances_percent: {
    getHref: () => GAS_ACCOUNT_DEPOSIT_HREF,
    getCtaLabelKey: () => "layout.notificationCtaDepositMore",
  },
  balances_usd: {
    getHref: () => GAS_ACCOUNT_DEPOSIT_HREF,
    getCtaLabelKey: () => "layout.notificationCtaDepositMore",
  },
}

export function getNotificationBehavior(
  item: AppNotification
): NotificationBehavior | undefined {
  const dataType = getStringData(item, "type")
  return notificationBehaviors[dataType ?? item.type]
}

export function getNotificationHref(item: AppNotification): string | undefined {
  return getNotificationBehavior(item)?.getHref?.(item)
}

export function getNotificationCtaLabelKey(
  item: AppNotification
): string | undefined {
  return getNotificationBehavior(item)?.getCtaLabelKey?.(item)
}

export function getNotificationDisplayType(
  item: AppNotification
): AppNotification["type"] | undefined {
  return getNotificationBehavior(item)?.getDisplayType?.(item)
}

export function runNotificationReceiveBehavior(
  context: NotificationReceiveContext
) {
  getNotificationBehavior(context.item)?.onReceive?.(context)
}
